import styled from 'styled-components';

export const CalendarWrapper = styled.div`
  background: ${({ theme }) => theme.colors.bgSurface};
  border-radius: ${({ theme }) => theme.radii.xl};
  border: 1px solid ${({ theme }) => theme.colors.gray200};
  overflow: hidden;
`;

export const MonthNav = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray200};
`;

export const MonthTitle = styled.h2`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: ${({ theme }) => theme.fontSizes.xl};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  margin: 0;
  color: ${({ theme }) => theme.colors.textPrimary};
  text-transform: capitalize;
`;

export const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
`;

export const DayHeader = styled.div`
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[1]}`};
  text-align: center;
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.textMuted};
  text-transform: uppercase;
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray200};
  background: ${({ theme }) => theme.colors.gray50};
`;

export const DayCell = styled.div<{ $isToday?: boolean; $isOutside?: boolean; $hasCollision?: boolean }>`
  min-height: 100px;
  padding: ${({ theme }) => theme.spacing[1]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray100};
  border-right: 1px solid ${({ theme }) => theme.colors.gray100};
  cursor: pointer;
  transition: background ${({ theme }) => theme.transitions.fast};
  opacity: ${({ $isOutside }) => ($isOutside ? 0.4 : 1)};
  background: ${({ $isToday, $hasCollision, theme }) =>
    $hasCollision
      ? 'rgba(217, 119, 6, 0.08)'
      : $isToday
        ? theme.colors.primary50
        : 'transparent'};
  border-left: ${({ $hasCollision, theme }) =>
    $hasCollision ? `3px solid ${theme.colors.warning}` : 'none'};

  &:hover {
    background: ${({ theme }) => theme.colors.gray50};
  }

  @media (max-width: 768px) {
    min-height: 60px;
  }
`;

export const DayNumber = styled.div<{ $isToday?: boolean }>`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: ${({ $isToday, theme }) => ($isToday ? theme.fontWeights.bold : theme.fontWeights.normal)};
  color: ${({ $isToday, theme }) => ($isToday ? theme.colors.primary : theme.colors.textSecondary)};
  margin-bottom: ${({ theme }) => theme.spacing[1]};
  padding: 0 ${({ theme }) => theme.spacing[1]};

  ${({ $isToday, theme }) =>
    $isToday &&
    `
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: ${theme.radii.full};
    background: ${theme.colors.primary};
    color: ${theme.colors.textInverse};
  `}
`;

// Polish hearing types — colors keyed to design bundle (calendar-posiedzenia preview).
// Resolved against the theme so palette changes propagate. PosiedzenieNiejawne
// purple is the only literal (no purple in the theme palette yet).
import type { Theme } from '@/styles/theme';
const eventColor = (theme: Theme, type: string): string => {
  switch (type) {
    case 'Rozprawa': return theme.colors.danger;
    case 'PosiedzenieJawne': return theme.colors.info;
    case 'PosiedzenieNiejawne': return '#7c3aed';
    case 'Inne':
    default: return theme.colors.gray500;
  }
};

export const CalendarEvent = styled.div<{ $type: string }>`
  padding: 2px 4px;
  margin-bottom: 2px;
  border-radius: ${({ theme }) => theme.radii.sm};
  font-size: 0.6875rem;
  line-height: 1.3;
  cursor: pointer;
  background: ${({ $type, theme }) => `${eventColor(theme, $type)}15`};
  border-left: 2px solid ${({ $type, theme }) => eventColor(theme, $type)};
  color: ${({ theme }) => theme.colors.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover {
    background: ${({ $type, theme }) => `${eventColor(theme, $type)}25`};
  }
`;

export const CollisionBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 0.625rem;
  color: ${({ theme }) => theme.colors.warning};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  padding: 0 ${({ theme }) => theme.spacing[1]};
`;

export const MobileEventList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
`;

export const MobileEventCard = styled.div<{ $type: string }>`
  padding: ${({ theme }) => theme.spacing[3]};
  background: ${({ theme }) => theme.colors.bgSurface};
  border-radius: ${({ theme }) => theme.radii.lg};
  border-left: 3px solid ${({ $type, theme }) => eventColor(theme, $type)};
  box-shadow: ${({ theme }) => theme.shadows.xs};
`;

export const MonthNavSpacer = styled.div`
  width: 120px;
`;

export const MobileMonthTitle = styled.h2`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: 1.125rem;
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.primary};
  margin: 0;
`;

export const DayCellOverflow = styled.div`
  font-size: 0.625rem;
  color: ${({ theme }) => theme.colors.textMuted};
  padding-left: ${({ theme }) => theme.spacing[1]};
`;

export const MobileEventTime = styled.div`
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  font-size: ${({ theme }) => theme.fontSizes.sm};
`;

export const MobileEventSubject = styled.div`
  font-size: 0.8125rem;
`;

export const MobileEventLocation = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textMuted};
`;
